/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import clsx from 'clsx'
import React, { CSSProperties, type JSX } from 'react'
import { Icon, IconName } from '../../utils/Icon'
import { imagePath } from '../../utils/ImagePath'
import { Horizon } from '../map_facilities/Horizon'

type FlowContainerProps = {
  children: React.ReactNode
  style?: CSSProperties
}

export const FlowContainer = ({ children, ...props }: FlowContainerProps) => {
  return (
    <div css={flowContainerStyle} {...props}>
      {children}
    </div>
  )
}

const flowContainerStyle = css`
  position: relative;
  padding-left: 18px;

  &::before {
    pointer-events: none;
    content: '';
    /* ↓ "1px" が赤線1本あたりの高さ。変更したいときは2つとも変更する。 */
    background-image: linear-gradient(
      to bottom,
      #eb2d23,
      #eb2d23 1px,
      transparent 1px,
      transparent 8px
    );
    /* ↓ 左側の "4px" が横幅。右側の "4px" で線の間隔を調整。 */
    background-size: 4px 4px;
    background-repeat: repeat-y;
    position: absolute;
    top: 5px;
    bottom: 0px;
    left: 3px;
    right: 0;
    opacity: 0.5;
  }
`

type FlowHeadingProps = {
  children: React.ReactNode
  as?: React.ElementType<any>
  style?: CSSProperties
  isLast?: boolean
}
export const FlowHeading = ({
  children,
  as: Component = 'div',
  isLast,
  ...props
}: FlowHeadingProps) => {
  const circleStyle = css`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
  `
  return (
    <Component
      css={flowHeadingStyle}
      className={clsx(isLast && 'isLast')}
      {...props}
    >
      <FlowCircle css={circleStyle} />
      {children}
    </Component>
  )
}
const flowHeadingStyle = css`
  display: flex;
  margin-top: 30px;
  align-items: center;
  padding-left: 18px;
  margin-left: -18px;
  position: relative;
`
type FlowTitleProps = {
  children: React.ReactNode
  as?: React.ElementType<any>
  style?: CSSProperties
}
export const FlowTitle = ({
  children,
  as: Component = 'div',
  ...props
}: FlowTitleProps) => {
  return (
    <Component css={flowTitleStyle} {...props}>
      <span>{children}</span>
    </Component>
  )
}
const flowTitleStyle = css`
  position: relative;
  height: 22px;
  width: 106px;
  margin-top: 25px;
  margin-left: -15px;

  /* 背景画像 */
  background: url(${imagePath('usage_flow_heading_background.svg')}) no-repeat;

  /* 文字のスタイルと位置 */
  color: white;
  font-weight: bold;
  font-size: 12px;
  padding-left: 23px;
  padding-top: 1px;
`

export const FlowCircle = ({ ...props }) => {
  return <span css={flowCircleStyle} {...props}></span>
}

const flowCircleStyle = css`
  background: #ffffff;
  border: 2px solid #eb2d23;
  box-sizing: border-box;
  border-radius: 50%;
  width: 10px;
  height: 10px;
  display: inline-block;
`

type LastItemWrappweProps = {
  children: React.ReactNode
}

export const LastItemWrapper = ({ children }: LastItemWrappweProps) => {
  return <div css={LastItemWrapperStyle}>{children}</div>
}

// 不要な線を消す
const LastItemWrapperStyle = css`
  position: relative;
  &::before {
    pointer-events: none;
    content: '';
    position: absolute;
    top: 10px;
    bottom: 0px;
    left: -18px;
    right: 0;
    width: 10px;
    height: 100%;
    background: white;
  }
`
type TimeProps = {
  startTime: string
  endTime?: string
}
export const Time = ({ startTime, endTime }: TimeProps) => {
  return (
    <Horizon columnGap="2px" as="time" css={timeStyle}>
      <span>{startTime}</span>
      {endTime && (
        <React.Fragment>
          <span>–</span>
          <span>{endTime}</span>
        </React.Fragment>
      )}
    </Horizon>
  )
}

const timeStyle = css`
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
  color: #eb2d23;
`
type NoteProps = {
  children: React.ReactNode
}
export const Note = ({ children }: NoteProps) => {
  return <p css={noteStyle}>{children}</p>
}

const noteStyle = css`
  margin-top: 10px;
`
type LabelProps = {
  children: React.ReactNode
  as?: React.ElementType<any>
  style?: CSSProperties
}
export const Label = ({
  children,
  as: Component = 'div',
  ...props
}: LabelProps) => {
  return (
    <Component css={labelStyle} {...props}>
      {children}
    </Component>
  )
}

const labelStyle = css`
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
`

type LinkProps = JSX.IntrinsicElements['a'] & {
  beforeIcon?: IconName
}
export const Link = ({ children, ...props }: LinkProps) => {
  return (
    <Horizon as="a" css={linkStyle} {...props}>
      {props.beforeIcon && (
        <Icon
          iconName={props.beforeIcon}
          style={{
            width: '18px',
            height: '18px',
            marginRight: '5px',
          }}
        />
      )}
      <span>{children}</span>
      {props.target === '_blank' && (
        <Icon
          iconName="blank"
          style={{
            width: '14px',
            height: '14px',
            marginLeft: '4px',
          }}
        />
      )}
    </Horizon>
  )
}
const linkStyle = css`
  text-decoration-line: underline !important;
  &:visited,
  &:link {
    color: inherit;
  }
`

type ImageProps = JSX.IntrinsicElements['img']
export const Image = ({ ...props }: ImageProps) => {
  return (
    <div css={imageStyle}>
      <img {...props} />
    </div>
  )
}
const imageStyle = css`
  display: flex;
  justify-content: center;
  img {
    border: 1px solid #edeceb;
    box-sizing: border-box;
    border-radius: 15px;
    width: 220px;
  }
`
